import React, {createContext, useContext, useId, useState} from 'react'
import {createPortal} from 'react-dom'
import DataTable, {DEFAULT_PAGINATION_ICONS} from 'react-data-table-component'
import './AdminDataTable.css'

export const adminDataTableStyles = {
  table: {style: {border: '1px solid #323248', borderRadius: '8px 8px 0 0', backgroundColor: '#1e1e2d'}},
  headRow: {style: {minHeight: '58px', borderBottomColor: '#323248', backgroundColor: '#1e1e2d'}},
  headCells: {style: {color: '#00ff00', fontSize: '.82rem', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase'}},
  rows: {
    style: {minHeight: '70px', borderBottomColor: '#323248', backgroundColor: '#1e1e2d', color: '#cdcdde'},
    stripedStyle: {backgroundColor: '#20202f'},
    highlightOnHoverStyle: {backgroundColor: '#272738', borderBottomColor: '#323248', color: '#fff', outline: 'none'},
  },
  cells: {style: {paddingTop: '.75rem', paddingBottom: '.75rem', fontSize: '.88rem'}},
  noData: {style: {minHeight: '160px', backgroundColor: '#1e1e2d', color: '#7e8299'}},
  pagination: {
    style: {minHeight: '64px', border: '1px solid #323248', borderTop: 'none', borderRadius: '0 0 8px 8px', backgroundColor: '#1e1e2d', color: '#a1a5b7'},
    pageButtonsStyle: {fill: '#a1a5b7', '&:disabled': {fill: '#56566b'}, '&:hover:not(:disabled)': {backgroundColor: '#323248'}, '&:focus': {backgroundColor: '#323248'}},
  },
}

export const adminPaginationOptions = {
  rowsPerPageText: 'Filas por página:',
  rangeSeparatorText: 'de',
  selectAllRowsItem: true,
  selectAllRowsItemText: 'Todos',
}

const PaginationTopSlot = createContext(null)

function AdminPagination({rowsPerPage, rowCount, currentPage, onChangePage, onChangeRowsPerPage, paginationRowsPerPageOptions = [10, 15, 20, 25, 30], paginationComponentOptions, paginationIcons}) {
  const topSlot = useContext(PaginationTopSlot)
  const selectId = useId()
  const options = {...adminPaginationOptions, ...paginationComponentOptions}
  const icons = {...DEFAULT_PAGINATION_ICONS, ...paginationIcons}
  const lastPage = Math.ceil(rowCount / rowsPerPage)
  const first = (currentPage - 1) * rowsPerPage + 1
  const last = Math.min(currentPage * rowsPerPage, rowCount)
  const controls = [
    ['first', 'Primera página', 1, currentPage === 1],
    ['previous', 'Página anterior', currentPage - 1, currentPage === 1],
    ['next', 'Página siguiente', currentPage + 1, currentPage === lastPage],
    ['last', 'Última página', lastPage, currentPage === lastPage],
  ]
  return <>
    {topSlot && !options.noRowsPerPage && createPortal(
      <div className='admin-table-page-size'>
        <label htmlFor={selectId}>{options.rowsPerPageText}</label>
        <select id={selectId} value={rowsPerPage} onChange={(event) => onChangeRowsPerPage(Number(event.target.value), currentPage)}>
          {paginationRowsPerPageOptions.map((count, index) => <option key={index} value={count}>{count}</option>)}
          {options.selectAllRowsItem && <option value={rowCount}>{options.selectAllRowsItemText}</option>}
        </select>
      </div>, topSlot
    )}
    <nav className='admin-table-pagination' aria-label='Paginación de tabla'>
      <span className='admin-table-pagination-range'>{first}–{last} {options.rangeSeparatorText} {rowCount}</span>
      <div className='admin-table-pagination-buttons'>
        {controls.map(([icon, label, page, disabled]) => <button key={icon} type='button' aria-label={label} disabled={disabled} onClick={() => onChangePage(page, rowCount)}>{icons[icon]}</button>)}
      </div>
    </nav>
  </>
}

export function AdminDataTable({className = '', paginationComponentOptions, customStyles, ...props}) {
  const [topSlot, setTopSlot] = useState(null)
  return <PaginationTopSlot.Provider value={topSlot}><div className={`admin-datatable ${className}`.trim()}>
    <div className='admin-table-pagination-top' ref={setTopSlot} />
    <DataTable
      customStyles={customStyles || adminDataTableStyles}
      paginationComponentOptions={paginationComponentOptions || adminPaginationOptions}
      persistTableHead
      responsive
      striped
      highlightOnHover
      paginationComponent={AdminPagination}
      {...props}
    />
  </div></PaginationTopSlot.Provider>
}

export function AdminTableSearch({value, onChange, placeholder = 'Buscar...', ariaLabel = 'Buscar'}) {
  return <div className='admin-table-toolbar'>
    <div className='admin-table-search'>
      <i className='fas fa-search' aria-hidden='true'></i>
      <input type='search' className='form-control' aria-label={ariaLabel} placeholder={placeholder} value={value} onChange={onChange} />
    </div>
  </div>
}

export function AdminTableActions({children}) {
  return <div className='admin-table-actions'>{children}</div>
}

export function AdminTableAction({variant, title, onClick, icon}) {
  return <button type='button' className={`admin-table-action admin-table-action-${variant}`} title={title} aria-label={title} onClick={onClick}>
    <i className={icon} aria-hidden='true'></i>
  </button>
}

export function AdminStatusBadge({status}) {
  const normalized = `${status || ''}`.toLocaleLowerCase('es-MX')
  const active = normalized === 'activo' || normalized === 'activa'
  const pending = normalized === 'por activar'
  return <span className={`admin-status-badge admin-status-badge-${pending ? 'pending' : active ? 'active' : 'inactive'}`}>{status}</span>
}
